import { useEffect, useId, useRef } from 'react';
import type { ChangeEvent, InputHTMLAttributes, JSX, ReactNode } from 'react';
import { useFieldAria } from './Field';
import { FOCUS_RING, cx } from './styles';

/*
 * 三态布尔。
 *
 * `'unknown'` 不是"为了完整而加的一档"，它是地面站里最真实的状态：
 * 参数尚未读取、遥测链路刚连上还没收到心跳、飞控没有回 ACK —— 这时参数的值
 * 是**未知**，不是"关"。把它显示成"关"（未勾选），用户会以为自己看到的是一个
 * 已经生效的设置，于是在错误的前提上做决策。
 */
export type TriState = boolean | 'unknown';

export interface CheckboxProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'aria-describedby' | 'checked' | 'children' | 'className' | 'onChange' | 'type' | 'value'
  > {
  value: TriState;
  /**
   * 点击后的目标值。三态下点一下 = "置为真"（用户表达的是"我要它是真"），
   * 想表达"关"就再点一次。中间态只是**当前读不到**，不是第三个可选项。
   */
  onChange: (next: boolean) => void;
  /** 自包含用法时的标题文案；在 `Field` 里请用 Field 的 label */
  label?: ReactNode;
  /** 补充说明（会接进 `aria-describedby`，读屏器读得到） */
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
}

export function Checkbox({
  value,
  onChange,
  label,
  description,
  disabled = false,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: CheckboxProps): JSX.Element {
  const field = useFieldAria();
  const generatedId = useId().replaceAll(':', '');
  const controlId = field === null ? (id ?? `checkbox-${generatedId}`) : field.controlId;
  const descriptionId = description === undefined ? undefined : `${controlId}-description`;
  const inputRef = useRef<HTMLInputElement>(null);
  const unknown = value === 'unknown';
  const isDisabled = disabled || field?.disabled === true;
  const isInvalid = field?.invalid === true;
  const describedBy = cx(field?.describedBy, descriptionId, ariaDescribedBy);

  useEffect(() => {
    /*
     * `indeterminate` 只有 DOM 属性、没有 HTML 属性，React 也没有对应的 prop 通道，
     * 必须命令式设置。这不违反"受控"：勾选状态仍然完全由 `value` 决定，
     * 这里只是把同一个值额外投影到 DOM 的三态表示上。
     */
    if (inputRef.current !== null) inputRef.current.indeterminate = unknown;
  }, [unknown]);

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    onChange(event.target.checked);
  }

  const control = (
    <input
      {...rest}
      ref={inputRef}
      id={controlId}
      type="checkbox"
      checked={value === true}
      /*
       * 三态必须能被**断言**出来，而不只是看起来不同：
       * 未知 → aria-checked="mixed" + DOM indeterminate（浏览器画成横杠）；
       * 假   → aria-checked="false"，没有横杠。
       * 读屏器与测试都靠这两个通道区分，颜色不参与。
       */
      aria-checked={unknown ? 'mixed' : value === true}
      onChange={handleChange}
      disabled={isDisabled}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      aria-invalid={isInvalid ? true : undefined}
      className={cx('mt-0.5 h-4 w-4 shrink-0 accent-accent', FOCUS_RING, className)}
    />
  );

  if (label === undefined && description === undefined) return control;

  return (
    <div className="flex flex-col gap-0.5">
      {label === undefined ? (
        control
      ) : (
        <label htmlFor={controlId} className={cx('inline-flex items-start gap-2 text-sm text-fg', isDisabled && 'opacity-50')}>
          {control}
          <span>{label}</span>
        </label>
      )}
      {description === undefined ? null : (
        // 说明放在 label **外面**：否则它会被算进可访问名，把"启用"读成一大段话
        <p id={descriptionId} className="m-0 pl-6 text-xs text-fg-subtle">
          {description}
        </p>
      )}
    </div>
  );
}
